import { registerCondition } from "../slices/conditionSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetches a Condition resource by ID:
 * - Checks the Redux store first
 * - If not present, fetches from FHIR server and registers it
 * - Logs an error AuditEvent if fetch fails
 *
 * @param {string} conditionId
 * @param {object} fhirClient
 * @param {object} store - Redux store instance
 * @param {object} options
 * @param {boolean} options.forceFetch
 * @returns {object|null} Condition resource or null if not found
 */
export const fetchCondition = async (
    conditionId,
    fhirClient,
    store,
    { forceFetch = false } = {}
) => {
    if (!conditionId || !fhirClient) return null;

    const cached = store.getState().conditions?.[conditionId];
    if (cached && !forceFetch) {
        return cached;
    }

    try {
        const fetched = await fhirClient.request(`Condition/${conditionId}`);
        store.dispatch(registerCondition(fetched));
        return fetched;
    } catch (err) {
        console.error("❌ Failed to fetch Condition:", err);
        const status = err?.response?.status || err?.status || 500;

        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "Condition fetch failed",
                status,
                resourceId: `Condition/${conditionId}`,
            },
            description: `Failed to retrieve Condition/${conditionId} from FHIR server`,
            store,
        });

        return { error: status };
    }
};

/**
 * Fetches a Condition resource by reference string (e.g., "Condition/abc123")
 * - Validates format
 * - Extracts ID and delegates to fetchCondition
 *
 * @param {string} ref
 * @param {object} fhirClient
 * @param {object} store - Redux store instance
 * @param {object} options
 * @returns {object|null} Condition resource or null if not found
 */
export const fetchConditionByReference = async (
    ref,
    fhirClient,
    store,
    options = {}
) => {
    if (!ref || !ref.startsWith("Condition/") || ref === "Condition/undefined") {
        console.warn("⚠️ Invalid Condition reference:", ref);
        return null;
    }

    const conditionId = ref.split("/")[1];
    if (!conditionId) {
        console.warn("⚠️ Could not extract Condition ID from reference:", ref);
        return null;
    }

    return fetchCondition(conditionId, fhirClient, store, options);
};
